import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
type Vertical = 'odonto' | 'estetica';
const content: Record<Vertical, {
  label: string;
  question: string;
  aiReply: string;
  service: string;
}> = {
  odonto: {
    label: 'Clínicas Odontológicas',
    question: 'Hola, quiero información sobre diseño de sonrisa.',
    aiReply: 'Claro. ¿Es tu primera consulta con nosotros?',
    service: 'Diseño de sonrisa'
  },
  estetica: {
    label: 'Clínicas Estéticas',
    question: 'Hola, quiero información sobre un tratamiento facial.',
    aiReply: 'Claro, cuéntame qué resultado buscas y te oriento mejor.',
    service: 'Tratamiento facial'
  }
};
const STEP_DELAYS = [200, 1300, 2600, 3800, 5000, 6200];
export function DemoSection() {
  const [vertical, setVertical] = useState<Vertical>('odonto');
  const [step, setStep] = useState(0);
  const [playKey, setPlayKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    amount: 0.4
  });
  const data = content[vertical];
  useEffect(() => {
    if (!isInView) return;
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setStep(6);
      return;
    }
    const timers = STEP_DELAYS.map((delay, idx) => setTimeout(() => setStep(idx + 1), delay));
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isInView, playKey]);
  const replay = () => {
    setStep(0);
    setPlayKey(k => k + 1);
  };
  const selectVertical = (key: Vertical) => {
    if (key === vertical) return;
    setVertical(key);
    replay();
  };
  return <section id="demo" className="bg-arena py-[72px] md:py-[120px]" data-id="element-118" data-mp="fs7i6qtx9vvc">
      <div className="max-w-[1000px] mx-auto px-6 md:px-20" data-id="element-119" data-mp="fput7tpqyl8s">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="text-center max-w-[560px] mx-auto mb-10" data-id="element-120" data-mp="ks3mysag1rby">
          <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-[0.15em] mb-4" data-id="element-121" data-mp="2lryugv9cz37e">
            EN ACCIÓN
          </span>
          <h2 className="font-display text-[32px] md:text-[44px] font-bold text-carbon leading-[1.1]" data-id="element-122" data-mp="2vjmi0l2so03l">
            Mira lo que realmente estás comprando.
          </h2>
        </motion.div>

        {/* Vertical toggle */}
        <div className="flex items-center justify-center gap-2 mb-3 flex-wrap" data-id="element-123" data-mp="34l2quodynu5p">
          {(Object.keys(content) as Vertical[]).map(key => <button key={key} onClick={() => selectVertical(key)} className={`font-body text-[13px] font-medium px-5 py-2.5 rounded-[6px] border transition-all duration-200 ${vertical === key ? 'bg-verde text-arena border-verde' : 'bg-transparent text-carbon/60 border-carbon/15 hover:text-carbon hover:border-carbon/30'}`} data-id="element-124" data-mp="1f6o2w6arobb9">
              {content[key].label}
            </button>)}
        </div>
        <p className="text-center font-body text-[12px] text-carbon/40 italic mb-12 max-w-[440px] mx-auto" data-id="element-125" data-mp="3322ndl3q7ne7">
          Este es nuestro enfoque inicial — ORIGEN se adapta a otros negocios
          con procesos de atención y agendamiento.
        </p>

        {/* Demo card */}
        <div ref={containerRef} className="bg-carbon rounded-[12px] p-6 md:p-10 max-w-[520px] mx-auto" data-id="element-126" data-mp="2y3j4xuocctql">
          <div className="space-y-3 min-h-[300px]" data-id="element-127" data-mp="re4hclsv0ojl">
            {/* Patient message */}
            <motion.div animate={step >= 1 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-end" data-id="element-128" data-mp="wcedaymert69">
              <div className="bg-arena text-carbon font-body text-[14px] rounded-[12px] rounded-tr-none px-4 py-2.5 max-w-[85%]" data-id="element-129" data-mp="2egazp4qoc746">
                {data.question}
              </div>
            </motion.div>

            {/* AI reply */}
            <motion.div animate={step >= 2 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-start" data-id="element-130" data-mp="24of3jz85er2l">
              <div className="bg-verde text-arena font-body text-[14px] rounded-[12px] rounded-tl-none px-4 py-2.5 max-w-[85%]" data-id="element-131" data-mp="1lsxbbv11kjvo">
                {data.aiReply}
              </div>
            </motion.div>

            {/* Lead calificado */}
            <motion.div animate={step >= 3 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3 mt-4" data-id="element-132" data-mp="1lkjzkd1z5bhg">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-azul mb-1.5" data-id="element-133" data-mp="12tao3y8n8cn8">
                LEAD CALIFICADO
              </span>
              <div className="font-body text-[13px] text-arena/80 space-y-0.5" data-id="element-134" data-mp="a6pwcwo99yj5">
                <p data-id="element-135" data-mp="f4rcb8qdxhqe">Servicio: {data.service}</p>
                <p data-id="element-136" data-mp="f3gctl3z2w53">Interés: Alto</p>
                <p data-id="element-137" data-mp="74bor5lm9v96">Fuente: WhatsApp</p>
              </div>
            </motion.div>

            {/* Agenda */}
            <motion.div animate={step >= 4 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3" data-id="element-138" data-mp="25i7r374fm49">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-salvia mb-2" data-id="element-139" data-mp="3fdbjix6kv2bk">
                AGENDA
              </span>
              <div className="flex gap-2" data-id="element-140" data-mp="3pa7wbf3iealx">
                <span className="font-body text-[12px] text-arena/80 bg-arena/10 rounded-full px-3 py-1" data-id="element-141" data-mp="3p957rdtnsk96">
                  Mañana 10:00 AM
                </span>
                <span className="font-body text-[12px] text-arena/80 bg-arena/10 rounded-full px-3 py-1" data-id="element-142" data-mp="14k8qpc9xsdmh">
                  3:00 PM
                </span>
              </div>
            </motion.div>

            {/* Cita confirmada */}
            <motion.div animate={step >= 5 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="font-body text-[14px] font-medium text-arena flex items-center gap-2 pt-1" data-id="element-143" data-mp="1h34cxduczqjt">
              CITA CONFIRMADA <span className="text-verde" data-id="element-144" data-mp="1m27j51m2s2td">✓</span>
            </motion.div>

            {/* Seguimiento automático */}
            <motion.div animate={step >= 6 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="font-body text-[14px] font-medium text-arena/70 flex items-center gap-2" data-id="element-145" data-mp="1q1oo7txlaxy0">
              SEGUIMIENTO AUTOMÁTICO <span className="text-verde" data-id="element-146" data-mp="1k3wg2kkcg8zb">✓</span>
            </motion.div>
          </div>

          <button onClick={replay} className="mt-6 inline-flex items-center gap-1.5 font-body text-[12px] text-arena/50 hover:text-arena/80 transition-opacity duration-150" data-id="element-147" data-mp="1f4jip5vujjih">
            <RotateCcw size={12} data-id="element-148" data-mp="27tgzt5obna9n" /> Ver de nuevo
          </button>
        </div>
      </div>
    </section>;
}